import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError, type AdminChild } from "@alight/contracts";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@alight/ui";
import { AlertTriangle } from "lucide-react";

/**
 * START-OF-SCHOOL-YEAR REVIEW — move children up a year, once, deliberately.
 *
 * There is no automatic January job on purpose. The term form records a BAND
 * ("Years 3-4"), not a single year, so nothing can work out whether a child in
 * that band moves up to "Years 5-6" or stays where they are for another year —
 * the stored data simply doesn't say which of the two years they're in. A
 * person knows; a scheduled job would have to guess, silently, for every
 * family, every January.
 *
 * So this screen does the one thing software is actually good at here: it puts
 * every child in front of the host at once, shows what they're recorded as now,
 * flags the records that look wrong, and saves the lot in a single pass. Every
 * change lands in the same correction history as any other edit.
 */

/** Cleared/never answered. Radix forbids an empty SelectItem value, so the
 *  "no year recorded" choice needs a sentinel of its own. */
const NOT_SET = "__not_set__";

/** Does this recorded year look out of step with the form's current options?
 *  Blank values and leftovers from the old spreadsheet import ("Year 3",
 *  "Year 7 and above") are exactly what the host wants to catch here. */
export function looksUnrecognised(value: string | null | undefined, options: string[]): boolean {
  const trimmed = (value ?? "").trim();
  if (trimmed === "") return true;
  return !options.includes(trimmed);
}

export function YearLevelReviewDialog({
  api,
  open,
  onOpenChange,
  students,
}: {
  api: ReturnType<typeof apiClient>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The ACTIVE children — archived families aren't part of a new school year.
   *  Named `students`, not `children`: React reserves that prop name, and an
   *  array of records sitting in it is a crash waiting for the first person who
   *  renders it. */
  students: AdminChild[];
}) {
  const qc = useQueryClient();

  // The year-level choices come from the live enrolment form, so this screen
  // and the form parents fill in can never offer different answers.
  const formQuery = useQuery({
    queryKey: ["admin", "forms", "enrolment"],
    queryFn: () => api.adminListForms("enrolment"),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: open,
  });
  const options = React.useMemo(
    () => formQuery.data?.find((f) => f.name === "year_level")?.options ?? [],
    [formQuery.data],
  );

  const [picked, setPicked] = React.useState<Record<number, string>>({});

  const rows = React.useMemo(
    () =>
      [...students].sort(
        (a, b) =>
          (a.year_level ?? "").localeCompare(b.year_level ?? "") ||
          a.name.localeCompare(b.name),
      ),
    [students],
  );

  /** What each child would become — their pick, or their current value. */
  function valueFor(child: AdminChild): string {
    return picked[child.id] ?? (child.year_level?.trim() || NOT_SET);
  }

  const changes = React.useMemo(
    () =>
      rows
        .filter((child) => {
          const next = valueFor(child);
          const now = child.year_level?.trim() || NOT_SET;
          return next !== now;
        })
        .map((child) => {
          const next = valueFor(child);
          return { id: child.id, year_level: next === NOT_SET ? null : next };
        }),
    // valueFor reads `picked`, so both drive this.
    [rows, picked],
  );

  const save = useMutation({
    mutationFn: () => api.adminReviewYearLevels({ changes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
      setPicked({});
      onOpenChange(false);
    },
  });

  const unrecognised = rows.filter((c) => looksUnrecognised(c.year_level, options)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Start-of-year review</DialogTitle>
          <DialogDescription>
            Set each child&apos;s year for the new school year. Nothing changes until you save,
            and only the ones you actually change are touched.
          </DialogDescription>
        </DialogHeader>

        {unrecognised > 0 ? (
          <div
            role="note"
            className="flex items-start gap-2 rounded-card border border-accent bg-accent/10 px-3 py-2 text-xs text-ink"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-fg" aria-hidden />
            <p>
              {unrecognised} {unrecognised === 1 ? "child is" : "children are"} recorded with a
              year that isn&apos;t one of the form&apos;s current options — usually a blank, or a
              single year left over from the old spreadsheet. They&apos;re marked below.
            </p>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto rounded-card border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b border-border text-left">
                <th className="px-3 py-2 font-semibold text-muted">Child</th>
                <th className="px-3 py-2 font-semibold text-muted">Recorded now</th>
                <th className="px-3 py-2 font-semibold text-muted">New year</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((child) => {
                const current = child.year_level?.trim() ?? "";
                const flagged = looksUnrecognised(child.year_level, options);
                // A legacy value has to stay pickable, or "leave it alone"
                // wouldn't be an option for the very rows that need review.
                const choices = current !== "" && !options.includes(current)
                  ? [current, ...options]
                  : options;
                return (
                  <tr key={child.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2">
                      <div className="font-medium text-ink">{child.name}</div>
                      <div className="text-xs text-muted">{child.parent_name ?? child.parent_email}</div>
                    </td>
                    <td className="px-3 py-2">
                      {current === "" ? (
                        <Badge variant="muted">Not set</Badge>
                      ) : (
                        <span className={flagged ? "text-accent-fg" : "text-ink"}>{current}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        value={valueFor(child)}
                        onValueChange={(v) =>
                          setPicked((prev) => ({ ...prev, [child.id]: v }))
                        }
                      >
                        <SelectTrigger
                          className="w-44"
                          aria-label={`New year level for ${child.name}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NOT_SET}>Not set</SelectItem>
                          {choices.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {save.error ? (
          <p className="text-sm text-accent-fg" role="alert">
            {save.error instanceof ApiError ? save.error.message : "Couldn’t save — please retry."}
          </p>
        ) : null}

        <DialogFooter className="items-center sm:justify-between">
          <span className="text-sm text-muted">
            {changes.length === 0
              ? "No changes yet"
              : `${changes.length} ${changes.length === 1 ? "child" : "children"} will change`}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending || changes.length === 0}
            >
              {save.isPending ? "Saving…" : "Save year levels"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
